import React, { useState, useEffect } from 'react';
import { companyInfo, contactInfo, navLinks } from '../data/mock';
import { Facebook, Linkedin, Twitter, Instagram, Youtube, ChevronRight, MapPin, Phone, Mail, ArrowUp } from 'lucide-react';

const socialIconMap = {
  facebook: Facebook,
  linkedin: Linkedin,
  twitter: Twitter,
  instagram: Instagram,
  youtube: Youtube
};

const Footer = () => {
  const [socialLinks, setSocialLinks] = useState({});

  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/social`);
        if (response.ok) {
          const data = await response.json();
          setSocialLinks(data);
        }
      } catch (error) {
        console.error('Failed to fetch social links:', error);
      }
    };
    fetchSocialLinks();
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#1e2430] relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#1a4db3]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-[#1f87ff]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img 
                src="/images/logo-white.png" 
                alt="Shree Silos Group" 
                loading="lazy"
                width="180"
                height="48"
                className="h-12 w-auto object-contain mb-2"
              />
              <p className="text-xs text-[#718096] mt-1">Group of Companies • Since 1989</p>
            </div>
            <p className="text-[#a0aec0] leading-relaxed mb-4">
              Complete elevator kits and components manufacturer serving customers across India.
            </p>
            <div className="space-y-2">
              <p className="text-sm text-[#a0aec0]">
                <span className="text-white font-medium">Shree Industries</span> • Manufacturing
              </p>
              <p className="text-sm text-[#a0aec0]">
                <span className="text-white font-medium">Silos Elevators</span> • Solutions
              </p>
              <p className="text-sm text-[#a0aec0]">
                <span className="text-white font-medium">Silos Meta Technologies</span> • Tech
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-[#a0aec0] hover:text-[#1f87ff] transition-colors flex items-center gap-2 group"
                  >
                    <ChevronRight className="w-4 h-4 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Products</h4>
            <ul className="space-y-3">
              {['Traction Elevators', 'Hydraulic Elevators', 'Passenger Lifts', 'Hospital Lifts', 'Freight Lifts'].map((service) => (
                <li key={service}>
                  <button
                    onClick={() => scrollToSection('#products')}
                    className="text-[#a0aec0] hover:text-[#1f87ff] transition-colors flex items-center gap-2 group"
                  >
                    <ChevronRight className="w-4 h-4 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {service}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${contactInfo.phone}`} className="flex items-start gap-3 text-[#a0aec0] hover:text-[#1f87ff] transition-colors">
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>{contactInfo.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${contactInfo.email}`} className="flex items-start gap-3 text-[#a0aec0] hover:text-[#1f87ff] transition-colors">
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>{contactInfo.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-[#a0aec0]">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span className="whitespace-pre-line">{contactInfo.address}</span>
              </li>
            </ul>

            {/* Social Links */}
            <div className="flex gap-3 mt-6">
              {Object.entries(socialLinks)
                .filter(([key, value]) => value && value.enabled && value.url && value.url !== '#')
                .map(([key, value]) => {
                  const Icon = socialIconMap[key];
                  if (!Icon) return null;
                  return (
                    <a
                      key={key}
                      href={value.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Follow us on ${key}`}
                      className="w-10 h-10 bg-[#2d3748] hover:bg-[#1a4db3] rounded-lg flex items-center justify-center text-[#a0aec0] hover:text-white transition-all duration-300"
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-[#2d3748] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#a0aec0] text-sm text-center md:text-left">
            © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm text-[#a0aec0]">
            <button onClick={() => {}} className="hover:text-[#1f87ff] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1f87ff] rounded">Privacy Policy</button>
            <button onClick={() => {}} className="hover:text-[#1f87ff] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1f87ff] rounded">Terms of Service</button>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        aria-label="Scroll to top of page"
        className="fixed bottom-8 right-8 w-12 h-12 bg-[#1a4db3] hover:bg-[#1f87ff] text-white rounded-full shadow-lg shadow-blue-500/30 flex items-center justify-center transition-all duration-300 hover:-translate-y-1 z-50"
      >
        <ArrowUp className="w-5 h-5" />
      </button>
    </footer>
  );
};

export default Footer;
